import React from 'react';
import { useTranslation } from 'react-i18next';

import { Sheet, useSheetDismiss } from '@/nova/components/Sheet';
import { Button, Text } from '@/nova/components';
import { Body, DangerButton, FooterRow } from './DeactivateModal.styles';

export type DeactivateModalProps = {
  open: boolean;
  /** Feature name, interpolated into the title + body copy. */
  name: string;
  /** Deactivation request in flight — disables the actions and blocks a double submit. */
  busy?: boolean;
  onClose: () => void;
  /** Confirm deactivation — calls the `deactivate-item` endpoint (cancel-at-period-end). */
  onConfirm: () => void;
};

/**
 * Confirmation dialog for deactivating an enabled feature. There's no up/downgrade
 * path for an active feature, so "Deactivate" is the only action — and because it
 * affects players it asks first. `onConfirm` calls the `deactivate-item` endpoint
 * (cancel-at-period-end: the feature runs until it expires, then stops renewing —
 * see ../../missing-data.md).
 */
export const DeactivateModal = ({
  open,
  name,
  busy = false,
  onClose,
  onConfirm,
}: DeactivateModalProps): React.ReactElement | null => {
  const { t } = useTranslation();
  // Dismiss through the overlay so the dialog slides out; wired straight to onClose it
  // would unmount on the spot and blink away.
  const { closeHandle, dismiss } = useSheetDismiss(onClose);

  if (!open) {
    return null;
  }

  return (
    <Sheet
      // The Overlay re-runs the title through t() (a no-op on an already-resolved
      // string); wrapping it here is what makes the key extractable (prop literals
      // aren't harvested — see docs/i18n.md).
      title={t('Deactivate {{name}}?', { name })}
      onClose={onClose}
      closeHandle={closeHandle}
      footer={
        <FooterRow>
          <Button variant="outline" label={t('Cancel')} disabled={busy} onClick={dismiss} />
          <DangerButton type="button" disabled={busy} onClick={onConfirm}>
            {/* Literal `t()` calls so both labels are harvested (a dynamic `text={…}`
                expression is not extracted — see docs/i18n.md). */}
            <Text text={busy ? t('Deactivating…') : t('Deactivate')} tag="span" />
          </DangerButton>
        </FooterRow>
      }
    >
      <Body
        // Deactivation is cancel-at-period-end: the feature keeps running until it
        // expires, then stops renewing (see the deactivate-item semantics).
        text="{{name}} keeps running until the end of your current billing period, then stops renewing. You can re-activate it anytime."
        placeholders={{ name }}
      />
    </Sheet>
  );
};

export default DeactivateModal;
